import {
  Box,
  Button,
  Field,
  Flex,
  Grid,
  IconButton,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react";
import {
  Activity,
  Eye,
  EyeOff,
  KeyRound,
  LockKeyhole,
  Server,
  ShieldCheck,
} from "lucide-react";
import { useState, type FormEvent } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router";
import { ThemeToggle } from "../components/ui";
import {
  SessionLoadingScreen,
  useAuthSession,
} from "../providers/auth-session";

export function LoginPage() {
  const { status, checkError, login } = useAuthSession();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [secret, setSecret] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const requestedNext = searchParams.get("next") ?? "";
  const next = requestedNext.startsWith("/")
    && !requestedNext.startsWith("//")
    && !requestedNext.startsWith("/login")
    ? requestedNext
    : "/overview";

  if (status === "checking") return <SessionLoadingScreen />;
  if (status === "authenticated") return <Navigate to={next} replace />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const candidate = secret.trim();
    if (!candidate) {
      setError("Enter the server admin secret to continue.");
      return;
    }
    setSubmitting(true);
    setError(undefined);
    try {
      await login(candidate);
      setSecret("");
      navigate(next, { replace: true });
    } catch (loginError) {
      const statusCode = (loginError as { statusCode?: number }).statusCode;
      setError(
        statusCode === 401
          ? "The secret is not valid. Check it and try again."
          : loginError instanceof Error
            ? loginError.message
            : String(loginError),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Flex minH="100vh" bg="bg.canvas" color="fg.default" position="relative" overflow="hidden">
      <Box position="absolute" inset="0" bg="radial-gradient(circle at 18% 22%, rgba(99,102,241,.16), transparent 34%), radial-gradient(circle at 84% 82%, rgba(16,185,129,.08), transparent 30%)" pointerEvents="none" />
      <Flex position="absolute" top="5" right="5" zIndex="2">
        <ThemeToggle />
      </Flex>
      <Grid
        position="relative"
        zIndex="1"
        width="100%"
        maxW="1080px"
        mx="auto"
        px={{ base: "5", md: "8" }}
        py={{ base: "16", lg: "8" }}
        templateColumns={{ base: "1fr", lg: "minmax(0,1.15fr) minmax(400px,.85fr)" }}
        gap={{ base: "10", lg: "16" }}
        alignItems="center"
      >
        <Stack gap="8" maxW="560px">
          <Flex align="center" gap="3">
            <Flex boxSize="42px" borderRadius="4px" bg="brand.500" color="white" align="center" justify="center">
              <TradingMark />
            </Flex>
            <Box>
              <Text fontWeight="850" fontSize="md" lineHeight="1.1">Trading OS</Text>
              <Text mt="1" color="fg.muted" fontSize="10px" letterSpacing=".08em">CONTROL CENTER</Text>
            </Box>
          </Flex>

          <Box>
            <Text color="brand.400" fontSize="xs" fontWeight="800" letterSpacing=".08em" textTransform="uppercase">
              Restricted operations console
            </Text>
            <Text mt="3" fontSize={{ base: "3xl", md: "4xl" }} lineHeight="1.08" fontWeight="850" letterSpacing="-.035em">
              One secure session for every trading workflow.
            </Text>
            <Text mt="4" maxW="500px" color="fg.muted" fontSize="sm" lineHeight="1.8">
              Broker state, execution history and bot controls remain unavailable until the server verifies your admin secret.
            </Text>
          </Box>

          <Grid templateColumns={{ base: "1fr", sm: "repeat(3,1fr)" }} gap="3">
            <SecurityFact icon={ShieldCheck} label="Protected API" detail="Reads and controls" />
            <SecurityFact icon={Activity} label="Live runtime" detail="Real persisted state" />
            <SecurityFact icon={Server} label="Server session" detail="HttpOnly cookie" />
          </Grid>
        </Stack>

        <Box
          as="form"
          onSubmit={submit}
          bg="bg.panel"
          borderWidth="1px"
          borderColor="border.default"
          borderRadius="4px"
          boxShadow="0 26px 80px rgba(0,0,0,.26)"
          p={{ base: "5", md: "7" }}
        >
          <Stack gap="5">
            <Flex boxSize="44px" bg="rgba(99,102,241,.12)" color="brand.400" borderRadius="4px" align="center" justify="center">
              <Box as={LockKeyhole} boxSize="5" />
            </Flex>
            <Box>
              <Text fontSize="xl" fontWeight="820">Sign in</Text>
              <Text mt="1.5" fontSize="xs" color="fg.muted" lineHeight="1.7">
                Use the admin secret configured on this trading server.
              </Text>
            </Box>

            {(error || checkError) && (
              <Flex p="3" gap="2.5" borderWidth="1px" borderColor="rgba(244,63,94,.35)" bg="rgba(244,63,94,.08)" borderRadius="4px" align="flex-start">
                <Box as={ShieldCheck} boxSize="4" color="status.negative" mt="0.5" flexShrink="0" />
                <Text fontSize="xs" color="status.negative" lineHeight="1.6">{error ?? checkError}</Text>
              </Flex>
            )}

            <Field.Root gap="1.5">
              <Field.Label htmlFor="admin-secret" fontSize="10px" color="fg.muted" fontWeight="800" letterSpacing=".05em" textTransform="uppercase">
                Admin secret
              </Field.Label>
              <Box position="relative" width="100%">
                <Box as={KeyRound} boxSize="4" position="absolute" left="3" top="50%" transform="translateY(-50%)" color="fg.subtle" pointerEvents="none" zIndex="1" />
                <Input
                  id="admin-secret"
                  aria-label="Admin secret"
                  type={showSecret ? "text" : "password"}
                  value={secret}
                  onChange={(event) => setSecret(event.target.value)}
                  autoComplete="current-password"
                  pl="9"
                  pr="11"
                  width="100%"
                  size="lg"
                  bg="bg.subtle"
                  borderRadius="4px"
                  placeholder="Enter server secret"
                />
                <IconButton
                  type="button"
                  aria-label={showSecret ? "Hide secret" : "Show secret"}
                  size="sm"
                  variant="ghost"
                  position="absolute"
                  right="1.5"
                  top="50%"
                  transform="translateY(-50%)"
                  onClick={() => setShowSecret((value) => !value)}
                >
                  <Box as={showSecret ? EyeOff : Eye} boxSize="4" />
                </IconButton>
              </Box>
            </Field.Root>

            <Button type="submit" size="lg" bg="brand.500" color="white" disabled={submitting || !secret.trim()}>
              <Box as={LockKeyhole} boxSize="4" />
              {submitting ? "Verifying…" : "Open control center"}
            </Button>
            <Text textAlign="center" fontSize="10px" color="fg.subtle" lineHeight="1.6">
              The secret is sent once over the secure control channel and is never saved in browser storage.
            </Text>
          </Stack>
        </Box>
      </Grid>
    </Flex>
  );
}

function SecurityFact({ icon, label, detail }: { icon: typeof ShieldCheck; label: string; detail: string }) {
  return (
    <Flex p="3" gap="2.5" borderWidth="1px" borderColor="border.default" bg="bg.panel" borderRadius="4px" align="center">
      <Flex boxSize="30px" bg="bg.subtle" color="brand.400" borderRadius="4px" align="center" justify="center" flexShrink="0">
        <Box as={icon} boxSize="3.5" />
      </Flex>
      <Box minW="0">
        <Text fontSize="xs" fontWeight="750">{label}</Text>
        <Text mt="0.5" fontSize="9px" color="fg.muted">{detail}</Text>
      </Box>
    </Flex>
  );
}

function TradingMark() {
  return (
    <Box w="16px" h="18px" position="relative">
      <Box position="absolute" left="3px" top="4px" w="4px" h="10px" bg="white" borderRadius="2px" />
      <Box position="absolute" left="4.5px" top="0" w="1px" h="18px" bg="white" opacity=".8" />
      <Box position="absolute" right="2px" top="7px" w="4px" h="7px" bg="white" opacity=".75" borderRadius="2px" />
      <Box position="absolute" right="3.5px" top="3px" w="1px" h="14px" bg="white" opacity=".65" />
    </Box>
  );
}
